class FullpageScroll {
  constructor(element) {
    this.element = element;
    this.swiperElement = element.querySelector('.fullpage-swiper');
    this.section = element.closest('[data-section-id]');
    this.sectionId = this.section ? this.section.dataset.sectionId : null;

    if (!this.swiperElement) {
      console.error('Swiper element not found');
      return;
    }

    // Mobile detection
    this.mobileBreakpoint = 768;
    this.isMobile = window.innerWidth <= this.mobileBreakpoint;

    this.init();
  }

  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initSwiper();
    this.initVideoPlayers();
    this.attachEventListeners();
    this.attachResizeListener();
  }

  initSwiper() {
    const settings = this.getSectionSettings();

    this.swiper = new Swiper(this.swiperElement, {
      direction: 'vertical',
      slidesPerView: 1,
      spaceBetween: 0,
      speed: settings.scrollSpeed || 800,
      mousewheel: settings.enableMousewheel !== false ? {
        releaseOnEdges: true,
        sensitivity: 1,
        thresholdDelta: 50, // Require more scroll before triggering
        thresholdTime: 500, // Minimum time between slides
        forceToAxis: true,
      } : false,
      keyboard: settings.enableKeyboard !== false ? {
        enabled: true,
        onlyInViewport: true,
      } : false,
      pagination: settings.showPagination !== false ? {
        el: '.swiper-pagination',
        clickable: true,
      } : false,
      on: {
        slideChange: () => this.onSlideChange(),
        reachEnd: () => this.onReachEnd(),
        fromEdge: () => this.onFromEdge(),
        slideChangeTransitionStart: () => {
          // Disable navigation during transition to prevent double-scrolling
          if (this.swiper) {
            this.swiper.allowSlideNext = false;
            this.swiper.allowSlidePrev = false;
            this.swiper.allowTouchMove = false;
          }
        },
        slideChangeTransitionEnd: () => {
          // Re-enable navigation after transition + cooldown period
          setTimeout(() => {
            if (this.swiper) {
              this.swiper.allowSlideNext = true;
              this.swiper.allowSlidePrev = true;
              // Don't re-enable touch if mobile touch listener is managing it
              // (prevents Swiper from intercepting swipes on the last slide)
              if (!this.mobileTouchListenerActive) {
                this.swiper.allowTouchMove = true;
              }
            }
            // 遷移完了直後にモバイルタッチリスナーをアタッチ（遅延なし）
            if (this._pendingMobileTouchAttach) {
              this._pendingMobileTouchAttach = false;
              this.attachMobileTouchListener();
            }
          }, 50); // Short cooldown after transition completes
        },
      },
    });

    // Initialize flags
    this.footerVisible = false;
    this.footerTimer = null;
    this.swiperReenableTimer = null;
    this.swiperReenableTimer2 = null;
    this.mobileFooterScrollEnabled = false;
    this.mobileTouchListenerActive = false;
    this.footerSwipeBackTimer = null;
    this.scrollAnimationId = null;
    this.reattachTouchTimer = null;

    // グローバルタッチ状態の追跡（進行中ジェスチャーの引き継ぎ用）
    this._touchActive = false;
    this._lastTouchY = 0;
    document.addEventListener('touchstart', (e) => {
      this._touchActive = true;
      if (e.touches.length > 0) this._lastTouchY = e.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) this._lastTouchY = e.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchend', () => {
      this._touchActive = false;
    }, { passive: true });

    // First slide scroll threshold
    this.firstSlideScrollAccumulated = 0;
    this.firstSlideScrollThreshold = 200; // Require more scroll to leave first slide
    this.firstSlideWheelListener = null;

    // Add fullpage-active class for CSS targeting (avoids :has() compatibility issues)
    document.body.classList.add('fullpage-active');

    // Start with body scroll disabled
    this.disableBodyScroll();

    // Disable swiper mousewheel and touch until loading overlay is removed
    if (!document.body.classList.contains('loading-overlay-removed')) {
      if (this.swiper && this.swiper.params.mousewheel) {
        this.swiper.mousewheel.disable();
      }
      // Also disable touch on mobile
      if (this.swiper) {
        this.swiper.allowTouchMove = false;
      }
    }

    // Set up custom scroll handling for first slide
    this.setupFirstSlideScrollHandler();
  }

  setupFirstSlideScrollHandler() {
    // Wait for loading overlay to be removed before enabling scroll handler
    // This prevents loading animation scroll from triggering slide change
    if (document.body.classList.contains('loading-overlay-removed')) {
      // Loading already done, enable immediately
      if (this.swiper.activeIndex === 0 && !this.isMobile) {
        this.enableFirstSlideScrollHandler();
      }
    } else {
      // Wait for loading overlay to be removed
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class' &&
              document.body.classList.contains('loading-overlay-removed')) {
            observer.disconnect();
            // Add delay to let scroll/touch inertia settle
            setTimeout(() => {
              // Re-enable touch on mobile
              if (this.swiper) {
                this.swiper.allowTouchMove = true;
              }

              // Only apply extra scroll threshold on desktop
              if (this.swiper.activeIndex === 0 && !this.isMobile) {
                this.firstSlideScrollAccumulated = 0;
                this.enableFirstSlideScrollHandler();
              }
            }, 800);
          }
        });
      });
      observer.observe(document.body, { attributes: true });
    }
  }

  enableFirstSlideScrollHandler() {
    // Disable swiper mousewheel on first slide
    if (this.swiper && this.swiper.params.mousewheel) {
      this.swiper.mousewheel.disable();
    }

    // Reset accumulated scroll
    this.firstSlideScrollAccumulated = 0;

    // Remove existing listener if any
    if (this.firstSlideWheelListener) {
      document.removeEventListener('wheel', this.firstSlideWheelListener);
    }

    // Cooldown flag - ignore scroll events briefly after enabling
    let cooldownActive = true;
    setTimeout(() => {
      cooldownActive = false;
      // Reset accumulated scroll after cooldown to ignore any residual scroll
      this.firstSlideScrollAccumulated = 0;
    }, 400);

    this.firstSlideWheelListener = (e) => {
      // Ignore scroll events during cooldown period
      if (cooldownActive) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Only handle if on first slide
      if (this.swiper.activeIndex !== 0) {
        this.disableFirstSlideScrollHandler();
        return;
      }

      // Downward scroll
      if (e.deltaY > 0) {
        e.preventDefault();
        e.stopPropagation();

        this.firstSlideScrollAccumulated += e.deltaY;

        // Check if threshold reached
        if (this.firstSlideScrollAccumulated >= this.firstSlideScrollThreshold) {
          this.disableFirstSlideScrollHandler();
          this.swiper.slideNext();
        }
      } else if (e.deltaY < 0) {
        // Reset on upward scroll
        this.firstSlideScrollAccumulated = 0;
      }
    };

    document.addEventListener('wheel', this.firstSlideWheelListener, { passive: false });
  }

  disableFirstSlideScrollHandler() {
    // Remove listener
    if (this.firstSlideWheelListener) {
      document.removeEventListener('wheel', this.firstSlideWheelListener);
      this.firstSlideWheelListener = null;
    }

    // Re-enable swiper mousewheel
    if (this.swiper && this.swiper.params.mousewheel) {
      this.swiper.mousewheel.enable();
    }

    // Reset accumulated scroll
    this.firstSlideScrollAccumulated = 0;

    // Disable mobile touch handler if active
    this.disableFirstSlideTouchHandler();
  }

  // Mobile-specific: require intentional swipe to leave first slide
  setupFirstSlideTouchHandler() {
    // Disable Swiper's touch on first slide
    if (this.swiper) {
      this.swiper.allowTouchMove = false;
    }

    this.firstSlideTouchStartY = 0;
    this.firstSlideTouchAccumulated = 0;
    const TOUCH_THRESHOLD = 50; // Require this much swipe distance

    // Cooldown to ignore residual touches
    let cooldownActive = true;
    setTimeout(() => {
      cooldownActive = false;
      this.firstSlideTouchAccumulated = 0;
    }, 500);

    this.firstSlideTouchStartHandler = (e) => {
      if (cooldownActive) return;
      this.firstSlideTouchStartY = e.touches[0].clientY;
    };

    this.firstSlideTouchMoveHandler = (e) => {
      if (cooldownActive) return;
      if (this.swiper.activeIndex !== 0) {
        this.disableFirstSlideTouchHandler();
        return;
      }

      const currentY = e.touches[0].clientY;
      const deltaY = this.firstSlideTouchStartY - currentY; // Positive = swipe up

      if (deltaY > 0) {
        // Swiping up (to go to next slide)
        this.firstSlideTouchAccumulated = deltaY;

        if (this.firstSlideTouchAccumulated >= TOUCH_THRESHOLD) {
          this.disableFirstSlideTouchHandler();
          this.swiper.allowTouchMove = true;
          this.swiper.slideNext();
        }
      }
    };

    document.addEventListener('touchstart', this.firstSlideTouchStartHandler, { passive: true });
    document.addEventListener('touchmove', this.firstSlideTouchMoveHandler, { passive: true });
  }

  disableFirstSlideTouchHandler() {
    if (this.firstSlideTouchStartHandler) {
      document.removeEventListener('touchstart', this.firstSlideTouchStartHandler);
      this.firstSlideTouchStartHandler = null;
    }
    if (this.firstSlideTouchMoveHandler) {
      document.removeEventListener('touchmove', this.firstSlideTouchMoveHandler);
      this.firstSlideTouchMoveHandler = null;
    }
    // Re-enable Swiper touch
    if (this.swiper) {
      this.swiper.allowTouchMove = true;
    }
  }

  getSectionSettings() {
    // In a production environment, you might want to read these from data attributes
    // For now, using defaults that match the schema
    return {
      scrollSpeed: 800,
      enableMousewheel: true,
      enableKeyboard: true,
      showPagination: true,
    };
  }

  initVideoPlayers() {
    // Wait for Vimeo Player API to load
    if (typeof Vimeo === 'undefined') {
      setTimeout(() => this.initVideoPlayers(), 100);
      return;
    }

    const allIframes = this.element.querySelectorAll('[data-vimeo-player]');
    // Only initialize visible iframes (skip hidden mobile/desktop variants)
    const videoIframes = Array.from(allIframes).filter((iframe) => {
      const wrapper = iframe.closest('.fullpage-video-wrapper');
      if (!wrapper) return true;
      return wrapper.offsetParent !== null || getComputedStyle(wrapper).display !== 'none';
    });
    this.videoPlayers = [];

    videoIframes.forEach((iframe) => {
      const player = new Vimeo.Player(iframe);
      this.videoPlayers.push(player);

      // Mute by default
      player.setVolume(0);

      // Preload video: play briefly then pause to show first frame (not thumbnail)
      // This buffers the video so it's ready to play instantly
      player.play().then(() => {
        // Check if overlay is still present - if so, pause on first frame
        if (!document.body.classList.contains('loading-overlay-removed')) {
          player.pause();
          console.log('Video preloaded and paused on first frame');
        }
      }).catch((err) => {
        console.log('Video preload error:', err);
      });

      // 手動ループ：動画終了時にすぐ先頭に戻って再生（隙間なくループ）
      player.on('ended', () => {
        player.setCurrentTime(0).then(() => {
          player.play();
          console.log('Video looped manually (ended event)');
        });
      });

      // バックアップ：動画終了0.5秒前に先頭に戻る（より確実にシームレス）
      let videoDuration = 0;
      player.getDuration().then((duration) => {
        videoDuration = duration;
      });

      player.on('timeupdate', (data) => {
        if (videoDuration > 0 && data.seconds >= videoDuration - 0.5) {
          player.setCurrentTime(0).then(() => {
            player.play();
            console.log('Video looped manually (timeupdate)');
          });
        }
      });

      // Start playback when loading overlay is removed
      const startPlayback = () => {
        console.log('Starting video playback');
        player.play().catch((err) => {
          console.log('Video play error:', err);
        });
      };

      // Check if loading overlay is already removed
      if (document.body.classList.contains('loading-overlay-removed')) {
        startPlayback();
      } else {
        // Listen for loadingOverlayRemoved custom event (dispatched on window)
        window.addEventListener('loadingOverlayRemoved', startPlayback, { once: true });
      }

      // Find mute toggle button in the same slide (now outside .fullpage-slide-bg)
      const slide = iframe.closest('.fullpage-slide');
      const muteButton = slide ? slide.querySelector('[data-mute-toggle]') : null;

      if (muteButton) {
        muteButton.addEventListener('click', () => {
          player.getVolume().then((volume) => {
            if (volume === 0) {
              // Unmute
              player.setVolume(1);
              muteButton.classList.add('unmuted');
            } else {
              // Mute
              player.setVolume(0);
              muteButton.classList.remove('unmuted');
            }
          });
        });
      }
    });
  }

  onSlideChange() {
    const activeIndex = this.swiper.activeIndex;
    const isLastSlide = activeIndex === this.swiper.slides.length - 1;
    const isFirstSlide = activeIndex === 0;

    // Handle first slide scroll threshold (desktop only)
    if (isFirstSlide && !this.isMobile) {
      // Re-enable first slide scroll handler when returning to first slide
      setTimeout(() => {
        this.enableFirstSlideScrollHandler();
      }, 500);
    } else if (!isFirstSlide) {
      // Make sure first slide handler is disabled when not on first slide
      this.disableFirstSlideScrollHandler();
    }

    if (isLastSlide) {
      this.swiperElement.classList.add('on-last-slide');

      if (this.isMobile) {
        // Mobile: Swiper遷移完了時にタッチリスナーをアタッチ
        // slideChangeTransitionEndで再有効化されるが、遷移中のスワイプ誤検知を防ぐため
        // touchStartのタイミングで判定するので遅延は最小限でOK
        this._pendingMobileTouchAttach = true;
      } else {
        // Desktop: Wait for additional scroll down to show footer
        // Disable swiper mousewheel first, then listen for downward scroll
        if (this.swiper && this.swiper.params.mousewheel) {
          this.swiper.mousewheel.disable();
        }
        // Delay listening to avoid detecting the slide transition scroll (wait for inertia to settle)
        setTimeout(() => {
          this.listenForDownwardScroll();
        }, 1000);
      }
    } else {
      this.swiperElement.classList.remove('on-last-slide');

      if (this.isMobile) {
        // Mobile: Clear pending attach flag
        this._pendingMobileTouchAttach = false;
        if (this.mobileListenerTimer) {
          clearTimeout(this.mobileListenerTimer);
          this.mobileListenerTimer = null;
        }
        // Mobile: Remove touch listener and disable normal scrolling
        this.removeMobileTouchListener();
        if (this.mobileFooterScrollEnabled) {
          this.disableMobileFooterScroll();
        }
        // Re-enable Swiper touch after transition completes (with delay to prevent multi-slide skip)
        setTimeout(() => {
          if (this.swiper) {
            this.swiper.allowTouchMove = true;
          }
        }, 300);
      } else {
        // Desktop: Only hide footer if it's actually visible
        if (this.footerVisible) {
          this.hideFooter();
        }
      }

      // Cancel footer timer if user navigated away before footer appeared
      if (this.footerTimer) {
        clearTimeout(this.footerTimer);
        this.footerTimer = null;
      }
      // Remove any scroll listeners when leaving last slide
      if (this.wheelListener) {
        document.removeEventListener('wheel', this.wheelListener);
        this.wheelListener = null;
      }
      // Ensure swiper mousewheel is re-enabled when leaving last slide
      if (this.swiper && this.swiper.params.mousewheel) {
        this.swiper.mousewheel.enable();
      }
    }
  }

  onReachEnd() {
    // Last slide reached, footer will auto-show
  }

  onFromEdge() {
    // User scrolled back from last slide
  }

  autoShowFooter() {
    // Clear any existing timer
    if (this.footerTimer) {
      clearTimeout(this.footerTimer);
    }

    // Show footer after a delay (allow image to settle)
    this.footerTimer = setTimeout(() => {
      this.showFooter();
    }, 800);
  }

  showFooter() {
    document.body.classList.add('footer-visible');
    this.footerVisible = true;

    // Clear any pending re-enable timer since we're showing footer
    if (this.swiperReenableTimer) {
      clearTimeout(this.swiperReenableTimer);
      this.swiperReenableTimer = null;
    }

    // Disable swiper mousewheel when footer is visible
    if (this.swiper && this.swiper.params.mousewheel) {
      this.swiper.mousewheel.disable();
    }

    // Listen for upward scroll to hide footer
    this.listenForUpwardScroll();
  }

  hideFooter() {
    document.body.classList.remove('footer-visible');
    this.footerVisible = false;

    // Remove scroll listener
    if (this.wheelListener) {
      document.removeEventListener('wheel', this.wheelListener);
      this.wheelListener = null;
    }

    // Clear any existing re-enable timer
    if (this.swiperReenableTimer) {
      clearTimeout(this.swiperReenableTimer);
      this.swiperReenableTimer = null;
    }

    if (this.swiperReenableTimer2) {
      clearTimeout(this.swiperReenableTimer2);
      this.swiperReenableTimer2 = null;
    }

    // Disable swiper mousewheel AND slide navigation temporarily
    if (this.swiper) {
      if (this.swiper.params.mousewheel) {
        this.swiper.mousewheel.disable();
      }
      // Block all slide navigation to prevent inertia from triggering slide change
      this.swiper.allowSlideNext = false;
      this.swiper.allowSlidePrev = false;
    }

    // Set flag to block any scroll processing during cooldown
    this.hideFooterCooldown = true;

    // Attach wheel listener to consume and ignore all scroll events during cooldown
    const cooldownWheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('wheel', cooldownWheelHandler, { passive: false });

    // Wait for trackpad inertia to settle before re-enabling (longer delay for trackpads)
    this.swiperReenableTimer = setTimeout(() => {
      // Remove cooldown wheel handler
      document.removeEventListener('wheel', cooldownWheelHandler);
      this.hideFooterCooldown = false;

      // Re-enable slide navigation
      if (this.swiper) {
        this.swiper.allowSlideNext = true;
        this.swiper.allowSlidePrev = true;
      }

      // Additional delay before re-enabling mousewheel to ensure inertia is fully settled
      this.swiperReenableTimer2 = setTimeout(() => {
        // Re-enable mousewheel
        if (this.swiper && this.swiper.params.mousewheel) {
          this.swiper.mousewheel.enable();
        }

        const activeIndex = this.swiper.activeIndex;
        const isLastSlide = activeIndex === this.swiper.slides.length - 1;

        if (isLastSlide && !this.footerVisible) {
          // On last slide: listen for downward scroll to show footer again
          this.listenForDownwardScroll();
        }

        this.swiperReenableTimer2 = null;
      }, 200);

      this.swiperReenableTimer = null;
    }, 600);
  }

  listenForUpwardScroll() {
    // Remove existing listener if any
    if (this.wheelListener) {
      document.removeEventListener('wheel', this.wheelListener);
    }

    // Track if we're currently hiding footer to prevent multiple triggers
    let isHiding = false;

    this.wheelListener = (e) => {
      // Check if scrolling up (deltaY < 0)
      if (e.deltaY < 0 && this.footerVisible && !isHiding) {
        e.preventDefault();
        e.stopPropagation();
        isHiding = true;

        // Remove listener immediately so next scroll goes to swiper
        document.removeEventListener('wheel', this.wheelListener);
        this.wheelListener = null;

        this.hideFooter();
      }
    };

    document.addEventListener('wheel', this.wheelListener, { passive: false });
  }

  listenForDownwardScroll() {
    // Remove existing listener if any
    if (this.wheelListener) {
      document.removeEventListener('wheel', this.wheelListener);
    }

    // Track if we're currently showing footer to prevent multiple triggers
    let isShowing = false;
    // Accumulate scroll to require intentional scroll action
    let accumulatedScrollDown = 0;
    let accumulatedScrollUp = 0;
    const SCROLL_THRESHOLD_DOWN = 100; // Require at least this much scroll to show footer
    const SCROLL_THRESHOLD_UP = 150; // Require more scroll to go to previous slide (prevent accidental triggers)

    // Disable swiper mousewheel - we'll handle all scrolling manually
    if (this.swiper && this.swiper.params.mousewheel) {
      this.swiper.mousewheel.disable();
    }

    this.wheelListener = (e) => {
      // Check if on last slide
      const activeIndex = this.swiper.activeIndex;
      const isLastSlide = activeIndex === this.swiper.slides.length - 1;

      if (!isLastSlide) {
        // Not on last slide anymore, remove listener and re-enable swiper
        document.removeEventListener('wheel', this.wheelListener);
        this.wheelListener = null;
        if (this.swiper && this.swiper.params.mousewheel) {
          this.swiper.mousewheel.enable();
        }
        return;
      }

      // Prevent all scroll from propagating while on last slide
      e.preventDefault();
      e.stopPropagation();

      // Downward scroll - accumulate towards showing footer
      if (e.deltaY > 0 && !this.footerVisible && !isShowing) {
        accumulatedScrollDown += e.deltaY;
        // Reset upward accumulation when scrolling down
        accumulatedScrollUp = 0;

        // Only show footer after threshold is reached
        if (accumulatedScrollDown >= SCROLL_THRESHOLD_DOWN) {
          isShowing = true;
          accumulatedScrollDown = 0;

          this.showFooter();

          // Reset flag after footer animation completes
          setTimeout(() => {
            isShowing = false;
          }, 1000);
        }
      } else if (e.deltaY < 0 && !this.footerVisible) {
        // Upward scroll - accumulate towards going to previous slide
        accumulatedScrollUp += Math.abs(e.deltaY);
        // Reset downward accumulation when scrolling up
        accumulatedScrollDown = 0;

        // Only go to previous slide after threshold is reached
        if (accumulatedScrollUp >= SCROLL_THRESHOLD_UP) {
          // Remove listener before slide change
          document.removeEventListener('wheel', this.wheelListener);
          this.wheelListener = null;

          // Re-enable swiper and go to previous slide
          if (this.swiper && this.swiper.params.mousewheel) {
            this.swiper.mousewheel.enable();
          }
          this.swiper.slidePrev();
        }
      }
    };

    document.addEventListener('wheel', this.wheelListener, { passive: false });
  }

  // Mobile-specific methods
  attachMobileTouchListener() {
    // Don't attach if footer scroll is currently enabled
    if (this.mobileFooterScrollEnabled) {
      return;
    }

    // Remove any existing listeners first
    this.removeMobileTouchListener();

    // Disable Swiper touch on last slide so our listener can capture events
    if (this.swiper) {
      this.swiper.allowTouchMove = false;
    }
    this.mobileTouchListenerActive = true;

    let touchStartY = 0;
    let triggered = false; // Prevent multiple triggers per gesture
    let touchStarted = false;
    const swipeThreshold = 50;

    // 進行中のジェスチャーがあれば、現在位置を基準点として引き継ぐ
    // これにより「最後のスライドへのスワイプ」の続きでフッターに行ける
    if (this._touchActive && this._lastTouchY > 0) {
      touchStartY = this._lastTouchY;
      touchStarted = true;
    }

    this.mobileTouchStartListener = (e) => {
      touchStartY = e.touches ? e.touches[0].clientY : e.clientY;
      triggered = false;
      touchStarted = true;
    };

    this.mobileTouchMoveListener = (e) => {
      if (!e.touches || triggered || !touchStarted) return;

      const currentY = e.touches[0].clientY;
      const deltaY = touchStartY - currentY; // Positive = swipe up (scroll down)

      if (deltaY > swipeThreshold) {
        triggered = true;
        this.enableMobileFooterScroll();
      } else if (deltaY < -swipeThreshold) {
        triggered = true;
        this.removeMobileTouchListener();
        if (this.swiper) {
          // Keep allowTouchMove false during slide transition to prevent multi-slide skip
          this.swiper.slidePrev();
        }
      }
    };

    this.mobileTouchEndListener = () => {
      // Reset for next gesture
      triggered = false;
      touchStarted = false;
    };

    // Use document level to ensure we capture events
    // Add both touch and mouse events for dev tools compatibility
    document.addEventListener('touchstart', this.mobileTouchStartListener, { passive: true });
    document.addEventListener('touchmove', this.mobileTouchMoveListener, { passive: true });
    document.addEventListener('touchend', this.mobileTouchEndListener, { passive: true });
    document.addEventListener('mousedown', this.mobileTouchStartListener, { passive: true });
    document.addEventListener('mouseup', this.mobileTouchEndListener, { passive: true });

    // Also listen for wheel events (for dev tools mouse wheel)
    this.mobileWheelListener = (e) => {
      if (e.deltaY > 30) {
        // Scrolling down
        this.enableMobileFooterScroll();
      } else if (e.deltaY < -30) {
        // Scrolling up, go to previous slide
        this.removeMobileTouchListener();
        if (this.swiper) {
          // Keep allowTouchMove false during slide transition
          this.swiper.slidePrev();
        }
      }
    };
    document.addEventListener('wheel', this.mobileWheelListener, { passive: true });
  }

  removeMobileTouchListener() {
    if (this.mobileTouchStartListener) {
      document.removeEventListener('touchstart', this.mobileTouchStartListener);
      document.removeEventListener('mousedown', this.mobileTouchStartListener);
      this.mobileTouchStartListener = null;
    }
    if (this.mobileTouchMoveListener) {
      document.removeEventListener('touchmove', this.mobileTouchMoveListener);
      this.mobileTouchMoveListener = null;
    }
    if (this.mobileTouchEndListener) {
      document.removeEventListener('touchend', this.mobileTouchEndListener);
      document.removeEventListener('mouseup', this.mobileTouchEndListener);
      this.mobileTouchEndListener = null;
    }
    if (this.mobileWheelListener) {
      document.removeEventListener('wheel', this.mobileWheelListener);
      this.mobileWheelListener = null;
    }
    this.mobileTouchListenerActive = false;
    // Note: Don't re-enable Swiper touch here - let the caller decide
  }

  enableMobileFooterScroll() {
    // Prevent multiple calls
    if (this.mobileFooterScrollEnabled) return;

    this.mobileFooterScrollEnabled = true;

    // Remove touch listener since we're now in scroll mode
    this.removeMobileTouchListener();

    // Clear any inline overflow styles set by disableMobileFooterScroll
    // so the CSS !important rules from mobile-footer-scroll class can take effect
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    this.element.style.height = '';
    this.element.style.overflow = '';

    // Add class to enable normal scrolling
    document.documentElement.classList.add('mobile-footer-scroll');
    document.body.classList.add('mobile-footer-scroll');

    // Force synchronous layout recalculation so the page is scrollable
    // before the scroll animation starts (prevents scrollTo failing on re-entry)
    void document.body.offsetHeight;

    // Disable Swiper controls
    if (this.swiper) {
      this.swiper.allowTouchMove = false;
      if (this.swiper.params.mousewheel) {
        this.swiper.mousewheel.disable();
      }
    }

    // iOSはtouchstart時にスクロール可否を判定するため、
    // overflow: hiddenで始まったジェスチャーではネイティブスクロールが効かない。
    // 自動スクロールで画面の50%分をスムーズに表示する。
    const targetScroll = window.innerHeight * 0.5;
    const duration = 600;
    const startTime = performance.now();
    const startScroll = 0;

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      window.scrollTo(0, startScroll + targetScroll * easeOut);

      if (progress < 1) {
        this.scrollAnimationId = requestAnimationFrame(animateScroll);
      } else {
        this.scrollAnimationId = null;
        this.attachMobileScrollListener();
        this.attachFooterSwipeBackListener();
      }
    };

    this.scrollAnimationId = requestAnimationFrame(animateScroll);
  }

  attachFooterSwipeBackListener() {
    // Fallback: detect strong swipe down when near top of page to return to fullpage mode
    // Delay attachment to avoid catching the swipe that triggered footer scroll mode
    if (this.footerSwipeBackTimer) {
      clearTimeout(this.footerSwipeBackTimer);
    }
    this.footerSwipeBackTimer = setTimeout(() => {
      this.footerSwipeBackTimer = null;
      let touchStartY = 0;
      let touchStartScrollY = 0;
      let isValidTouch = false; // Only process touches that started after listener attached

      this.footerSwipeBackStart = (e) => {
        touchStartY = e.touches[0].clientY;
        touchStartScrollY = window.scrollY;
        isValidTouch = true;
      };

      this.footerSwipeBackMove = (e) => {
        // Only process if this touch started after listener was attached
        if (!isValidTouch) return;

        // Only trigger if we're near the top of the page
        if (window.scrollY > 50) return;

        const currentY = e.touches[0].clientY;
        const deltaY = currentY - touchStartY; // Positive = swipe down (going back up)

        // Strong swipe down while near top = return to fullpage
        if (deltaY > 100 && touchStartScrollY <= 20) {
          console.log('Swipe back detected, returning to fullpage mode');
          this.removeFooterSwipeBackListener();
          // swiper.update() and slideTo() are handled inside disableMobileFooterScroll's setTimeout
          this.disableMobileFooterScroll();
        }
      };

      this.footerSwipeBackEnd = () => {
        isValidTouch = false;
      };

      document.addEventListener('touchstart', this.footerSwipeBackStart, { passive: true });
      document.addEventListener('touchmove', this.footerSwipeBackMove, { passive: true });
      document.addEventListener('touchend', this.footerSwipeBackEnd, { passive: true });
    }, 500); // Wait for initial swipe to complete
  }

  removeFooterSwipeBackListener() {
    if (this.footerSwipeBackTimer) {
      clearTimeout(this.footerSwipeBackTimer);
      this.footerSwipeBackTimer = null;
    }
    if (this.footerSwipeBackStart) {
      document.removeEventListener('touchstart', this.footerSwipeBackStart);
      this.footerSwipeBackStart = null;
    }
    if (this.footerSwipeBackMove) {
      document.removeEventListener('touchmove', this.footerSwipeBackMove);
      this.footerSwipeBackMove = null;
    }
    if (this.footerSwipeBackEnd) {
      document.removeEventListener('touchend', this.footerSwipeBackEnd);
      this.footerSwipeBackEnd = null;
    }
  }

  disableMobileFooterScroll() {
    // Cancel any running scroll animation
    if (this.scrollAnimationId) {
      cancelAnimationFrame(this.scrollAnimationId);
      this.scrollAnimationId = null;
    }

    // Cancel near-top fallback timer
    if (this.nearTopTimer) {
      clearTimeout(this.nearTopTimer);
      this.nearTopTimer = null;
    }

    // Remove listeners first
    this.removeManualTouchScroll();
    this.removeFooterSwipeBackListener();

    // Remove scroll listener
    if (this.mobileScrollListener) {
      window.removeEventListener('scroll', this.mobileScrollListener);
      this.mobileScrollListener = null;
    }

    // ===== transform補正方式（overflow変更なし） =====
    // overflow:hiddenを設定するとscrollYが凍結しscrollTo(0,0)が効かなくなる。
    // mobile-footer-scrollクラスもそのまま残し（ページはscrollable）、
    // transform: translateY(-scrollY)で視覚補正しながらscrollTo(0,0)で自然に戻す。

    this.mobileFooterScrollEnabled = false;

    // フッターを即座に非表示（transformで上に移動中にフッターが見えないように）
    const footerGroup = document.querySelector('.shopify-section-group-footer-group');
    if (footerGroup) {
      footerGroup.style.visibility = 'hidden';
    }

    // 現在のスクロール位置をtransformで即座に補正
    const initialScroll = window.scrollY;
    if (initialScroll > 0) {
      this.element.style.transform = `translateY(${-initialScroll}px)`;
    }

    // scrollTo(0,0)でスクロール位置を戻し始める
    window.scrollTo(0, 0);

    // scrollYが0で安定するまでポーリング
    let stableFrames = 0;
    const requiredStableFrames = 3;
    const maxFrames = 90; // 1.5秒
    let frameCount = 0;

    const pollScroll = () => {
      frameCount++;
      const scrollY = window.scrollY;

      // scrollYが残っていればtransformで補正し続ける
      if (scrollY > 0) {
        this.element.style.transform = `translateY(${-scrollY}px)`;
        window.scrollTo(0, 0);
        stableFrames = 0;
      } else {
        // scrollY===0 → transform不要
        this.element.style.transform = '';
        stableFrames++;
      }

      if (stableFrames >= requiredStableFrames || frameCount >= maxFrames) {
        // scrollY===0で安定 → レイアウト切り替え
        this.element.style.transform = '';

        // クラス削除（fullpageモードに戻る）
        document.documentElement.classList.remove('mobile-footer-scroll');
        document.body.classList.remove('mobile-footer-scroll');

        document.documentElement.style.overflow = '';
        document.body.style.overflow = 'hidden';
        this.element.style.height = '';
        this.element.style.overflow = '';

        // フッター表示復帰（CSS制御に戻す）
        if (footerGroup) {
          footerGroup.style.visibility = '';
        }

        window.scrollTo(0, 0);
        void this.element.offsetHeight;

        // Swiperを再初期化
        if (this.swiper) {
          this.swiper.update();
          this.swiper.slideTo(this.swiper.slides.length - 1, 0);
        }

        // タッチリスナー再アタッチ
        if (this.reattachTouchTimer) {
          clearTimeout(this.reattachTouchTimer);
        }
        this.reattachTouchTimer = setTimeout(() => {
          this.reattachTouchTimer = null;
          const activeIndex = this.swiper ? this.swiper.activeIndex : 0;
          const isLastSlide = this.swiper && activeIndex === this.swiper.slides.length - 1;
          if (isLastSlide && this.isMobile) {
            this.attachMobileTouchListener();
          }
        }, 50);
        return;
      }

      requestAnimationFrame(pollScroll);
    };

    requestAnimationFrame(pollScroll);
  }

  attachMobileScrollListener() {
    // Remove existing listener if any
    if (this.mobileScrollListener) {
      window.removeEventListener('scroll', this.mobileScrollListener);
    }
    if (this.nearTopTimer) {
      clearTimeout(this.nearTopTimer);
      this.nearTopTimer = null;
    }

    let ticking = false;
    let lastScrollY = window.scrollY;
    let hasScrolledDown = window.scrollY > 50; // Already scrolled down if animation placed us there

    this.mobileScrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';
          lastScrollY = currentScrollY;

          // Mark that user has scrolled down
          if (scrollDirection === 'down' && currentScrollY > 50) {
            hasScrolledDown = true;
          }

          // Return to fullpage if scrolled near top (threshold 15px instead of 0
          // to handle momentum settling at small positive values)
          if (hasScrolledDown && scrollDirection === 'up' && currentScrollY < 15) {
            console.log('Scrolled back to top, returning to fullpage mode');
            this.disableMobileFooterScroll();
          }

          // Fallback: if scroll settles near top but not quite at 0,
          // set a timer to auto-return. This catches cases where
          // pointer-events:none prevents further scroll events from firing.
          if (this.nearTopTimer) {
            clearTimeout(this.nearTopTimer);
          }
          if (currentScrollY < 50 && hasScrolledDown) {
            this.nearTopTimer = setTimeout(() => {
              this.nearTopTimer = null;
              if (this.mobileFooterScrollEnabled && window.scrollY < 50) {
                console.log('Stuck near top, auto-returning to fullpage mode');
                this.disableMobileFooterScroll();
              }
            }, 400);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    // Delay attaching listener to prevent immediate trigger
    setTimeout(() => {
      window.addEventListener('scroll', this.mobileScrollListener, { passive: true });
      console.log('Mobile scroll listener attached');
    }, 300);

    // Setup touch scroll that doesn't kill native momentum
    this.setupManualTouchScroll();
  }

  setupManualTouchScroll() {
    // Native scroll should work via CSS -webkit-overflow-scrolling: touch
    // No manual touch handling needed - just let the browser handle it
    console.log('Native touch scroll enabled via CSS');
  }

  removeManualTouchScroll() {
    if (this.manualTouchStart) {
      document.removeEventListener('touchstart', this.manualTouchStart);
      this.manualTouchStart = null;
    }
    if (this.manualTouchMove) {
      document.removeEventListener('touchmove', this.manualTouchMove);
      this.manualTouchMove = null;
    }
  }

  attachResizeListener() {
    let resizeTimer;
    this.resizeListener = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= this.mobileBreakpoint;

        // Handle transition between mobile and desktop
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            // Switched to mobile
            if (this.footerVisible) {
              this.hideFooter();
            }
            // If on last slide, attach touch listener
            const activeIndex = this.swiper ? this.swiper.activeIndex : 0;
            const isLastSlide = this.swiper && activeIndex === this.swiper.slides.length - 1;
            if (isLastSlide) {
              this.attachMobileTouchListener();
            }
          } else {
            // Switched to desktop
            this.removeMobileTouchListener();
            if (this.mobileFooterScrollEnabled) {
              this.disableMobileFooterScroll();
            }
          }
        }
      }, 150);
    };

    window.addEventListener('resize', this.resizeListener);
  }

  enableBodyScroll() {
    // Allow scrolling to footer
    document.body.style.overflow = '';
    document.body.classList.add('footer-visible');
    this.element.style.height = '';
  }

  disableBodyScroll() {
    // Prevent body scroll - keep locked to fullpage
    document.body.style.overflow = 'hidden';
    document.body.classList.remove('footer-visible');
    this.element.style.height = window.innerHeight + 'px';
  }

  attachEventListeners() {
    // Handle Shopify theme editor events
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', (event) => {
        if (event.detail.sectionId === this.sectionId) {
          this.destroy();
          this.init();
        }
      });

      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail.sectionId === this.sectionId) {
          this.destroy();
        }
      });

      document.addEventListener('shopify:block:select', (event) => {
        if (event.target.closest('[data-section-id]')?.dataset.sectionId === this.sectionId) {
          const blockElement = event.target;
          const slideIndex = Array.from(this.swiper.slides).indexOf(blockElement);
          if (slideIndex !== -1) {
            this.swiper.slideTo(slideIndex);
          }
        }
      });
    }
  }

  destroy() {
    // Clear all timers
    if (this.footerTimer) {
      clearTimeout(this.footerTimer);
      this.footerTimer = null;
    }

    if (this.swiperReenableTimer) {
      clearTimeout(this.swiperReenableTimer);
      this.swiperReenableTimer = null;
    }

    if (this.swiperReenableTimer2) {
      clearTimeout(this.swiperReenableTimer2);
      this.swiperReenableTimer2 = null;
    }

    // Remove wheel listener
    if (this.wheelListener) {
      document.removeEventListener('wheel', this.wheelListener);
      this.wheelListener = null;
    }

    // Remove mobile scroll listener
    if (this.mobileScrollListener) {
      window.removeEventListener('scroll', this.mobileScrollListener);
      this.mobileScrollListener = null;
    }

    // Clear mobile listener timers
    if (this.reattachTouchTimer) {
      clearTimeout(this.reattachTouchTimer);
      this.reattachTouchTimer = null;
    }
    if (this.mobileListenerTimer) {
      clearTimeout(this.mobileListenerTimer);
      this.mobileListenerTimer = null;
    }
    if (this.nearTopTimer) {
      clearTimeout(this.nearTopTimer);
      this.nearTopTimer = null;
    }

    // Cancel scroll animation
    if (this.scrollAnimationId) {
      cancelAnimationFrame(this.scrollAnimationId);
      this.scrollAnimationId = null;
    }

    // Remove mobile touch/wheel listeners
    this.removeMobileTouchListener();
    this.removeManualTouchScroll();
    this.removeFooterSwipeBackListener();
    if (this.mobileWheelListener) {
      document.removeEventListener('wheel', this.mobileWheelListener);
      this.mobileWheelListener = null;
    }

    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    // Clean up mobile classes and flags
    document.documentElement.classList.remove('mobile-footer-scroll');
    document.body.classList.remove('mobile-footer-scroll');
    document.body.classList.remove('fullpage-active');
    this.mobileFooterScrollEnabled = false;
    this.mobileTouchListenerActive = false;

    // Hide footer (without triggering swiper re-enable)
    document.body.classList.remove('footer-visible');
    this.footerVisible = false;

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.enableBodyScroll();
  }
}

// Initialize all fullpage scroll sections
function initFullpageScroll() {
  const sections = document.querySelectorAll('.fullpage-scroll-wrapper');
  sections.forEach(section => {
    new FullpageScroll(section);
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFullpageScroll);
} else {
  initFullpageScroll();
}

// Also initialize when Shopify section is loaded (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.querySelector('.fullpage-scroll-wrapper')) {
    const section = event.target.querySelector('.fullpage-scroll-wrapper');
    new FullpageScroll(section);
  }
});
